import React, { useState, useEffect } from 'react';
import { Save, ArrowRight, FileText, ExternalLink, RefreshCw, Upload } from 'lucide-react';
import { Button } from '../components/common/Button';
import { ResumeProtocol, UserPreferences } from '../types/resume';
import { ParticleBackground } from '../components/ParticleBackground';
import { ToastProvider, useToast } from '../components/common/Toast';
import { AutocompleteChipField } from '../components/common/AutocompleteChipField';
import { FreeTextChipField } from '../components/common/FreeTextChipField';
import { ResumePreviewModal } from '../components/resume/ResumePreviewModal';
import { ResumeUploadModal } from '../components/resume/ResumeUploadModal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import configService, { JobRole, ExperienceLevel, Language, InterviewGoal } from '../services/configService';
import careerService from '../services/careerService';
import LoadingIndicator from '../components/common/LoadingIndicator';

// Props interface for the ResumeCareerSetupPage component
interface ResumeCareerSetupPageProps {
  onComplete?: () => void;
  fromSource?: string; // This is used to determine where the user came from i.e which page they were on before this page
}

function ResumeCareerSetupContent({ onComplete, fromSource = 'onboarding' }: ResumeCareerSetupPageProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [resumes, setResumes] = useState<ResumeProtocol[]>([]);
  const [preferences, setPreferences] = useState<UserPreferences>({
    jobRoles: [],
    experienceLevel: '',
    languages: [],
    interviewGoals: []
  });
  const [isSaving, setIsSaving] = useState(false);
  const [selectedResume, setSelectedResume] = useState<ResumeProtocol | null>(null);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false);
  
  // State for config data
  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevel[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [interviewGoals, setInterviewGoals] = useState<InterviewGoal[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | undefined>(undefined);

  // Handle responsive layout
  useEffect(() => {
    const checkMobileView = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      setIsMobileView(isMobile);
      console.log('ResumeCareerSetupPage -> isMobileView', isMobile, 'width:', width);
    };

    // Initialize on mount with a small delay to ensure correct measurement
    checkMobileView();
    
    // Set up ResizeObserver for more reliable detection
    const resizeObserver = new ResizeObserver((entries) => {
      // We only care about the window size changes
      checkMobileView();
    });
    
    // Observe the document body for size changes
    resizeObserver.observe(document.body);
    
    // Also listen for window resize events as a fallback
    window.addEventListener('resize', checkMobileView);
    
    // Force a re-check after a short delay to handle some edge cases with dev tools
    setTimeout(checkMobileView, 300);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
      resizeObserver.disconnect();
    };
  }, []);

  // Hide validation error when form becomes valid
  useEffect(() => {
    if (isFormValid() && showValidationError) {
      setShowValidationError(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, showValidationError]);

  // Fetch configuration data from API
  useEffect(() => {
    const fetchConfigData = async () => {
      setIsLoadingConfig(true);
      setConfigError(undefined);
      
      try {
        // Fetch all config data in parallel
        const [
          jobRolesData,
          experienceLevelsData,
          languagesData,
          interviewGoalsData
        ] = await Promise.all([
          configService.getJobRoles(),
          configService.getExperienceLevels(),
          configService.getLanguages(),
          configService.getInterviewGoals()
        ]);
        
        // Log the received data for debugging
        console.log('ResumeCareerSetupPage: Received config data:', {
          jobRoles: jobRolesData.length,
          experienceLevels: experienceLevelsData.length,
          languages: languagesData.length,
          interviewGoals: interviewGoalsData.length
        });
        
        // More detailed logging of actual data
        console.log('ResumeCareerSetupPage: Sample job roles:', jobRolesData.slice(0, 2));
        console.log('ResumeCareerSetupPage: Sample experience levels:', experienceLevelsData.slice(0, 2));
        console.log('ResumeCareerSetupPage: Sample languages:', languagesData.slice(0, 2));
        console.log('ResumeCareerSetupPage: Sample interview goals:', interviewGoalsData.slice(0, 2));
        
        // Validate data before updating state
        const validJobRoles = Array.isArray(jobRolesData) && jobRolesData.length > 0 
          ? jobRolesData.filter(role => role && typeof role === 'object' && ('title' in role || 'name' in role))
          : [];
        
        const validExperienceLevels = Array.isArray(experienceLevelsData) && experienceLevelsData.length > 0
          ? experienceLevelsData.filter(level => level && typeof level === 'object' && 'name' in level)
          : [];
          
        const validLanguages = Array.isArray(languagesData) && languagesData.length > 0
          ? languagesData.filter(lang => lang && typeof lang === 'object' && 'name' in lang)
          : [];
          
        const validInterviewGoals = Array.isArray(interviewGoalsData) && interviewGoalsData.length > 0
          ? interviewGoalsData.filter(goal => goal && typeof goal === 'object' && 'name' in goal)
          : [];
        
        // Check if any of the arrays are empty, which might indicate a problem
        if (!validJobRoles.length && !validExperienceLevels.length && !validLanguages.length && !validInterviewGoals.length) {
          console.error('ResumeCareerSetupPage: All configuration data arrays are empty or invalid. This might indicate an API or data parsing issue.');
          
          if (jobRolesData.length > 0 || experienceLevelsData.length > 0 || languagesData.length > 0 || interviewGoalsData.length > 0) {
            console.error('ResumeCareerSetupPage: Data was received but failed validation. Original data structure may be unexpected.');
          }
        }
        
        // Update state with validated data
        setJobRoles(validJobRoles);
        
        // Sort experience levels by order field
        const sortedExperienceLevels = [...validExperienceLevels].sort((a, b) => a.order - b.order);
        setExperienceLevels(sortedExperienceLevels);
        
        setLanguages(validLanguages);
        setInterviewGoals(validInterviewGoals);
        
        console.log('Config data loaded successfully');
      } catch (err) {
        console.error('Failed to fetch configuration data:', err);
        setConfigError('Failed to load configuration data. Please try again.');
      } finally {
        setIsLoadingConfig(false);
      }
    };
    
    fetchConfigData();
  }, []);

  // Fetch resumes from API
  useEffect(() => {
    const fetchResumes = async () => {
      setLoading(true);
      setError(undefined);
      
      try {
        // Clear cache to ensure fresh data on component mount
        careerService.clearResumeCache();
        
        // Fetch resumes from careerService
        const resumeData = await careerService.getResumes();
        console.log('ResumeCareerSetupPage: Fetched resumes from API:', resumeData.length);
        setResumes(resumeData);
      } catch (err) {
        console.error('ResumeCareerSetupPage: Failed to fetch resumes:', err);
        setError('Failed to fetch resumes. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchResumes();
  }, []); // Empty dependency array to fetch only on mount

  const handleButtonClick = () => {
    if (!isFormValid()) {
      setShowValidationError(true);
    } else {
      handleSave();
    }
  };

  const handleRefreshResumes = async () => {
    // Refresh resumes from API
    setLoading(true);
    setError(undefined);
    
    try {
      // Force refresh from the API by passing true
      const resumeData = await careerService.getResumes(true);
      console.log('ResumeCareerSetupPage: Refreshed resumes from API:', resumeData.length);
      setResumes(resumeData);
      showToast('Resumes refreshed successfully', 'success');
    } catch (err) {
      console.error('ResumeCareerSetupPage: Failed to refresh resumes:', err);
      setError('Failed to refresh resumes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJobRolesChange = (jobRoles: string[]) => {
    console.log('ResumeCareerSetupPage: Job roles changed:', jobRoles);
    setPreferences({
      ...preferences,
      jobRoles
    });
  };

  const handleExperienceLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPreferences({
      ...preferences,
      experienceLevel: e.target.value
    });
  };

  const handleLanguagesChange = (languages: string[]) => {
    setPreferences({
      ...preferences,
      languages
    });
  };

  const handleInterviewGoalsChange = (interviewGoals: string[]) => {
    setPreferences({
      ...preferences,
      interviewGoals
    });
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      setShowValidationError(true);
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Convert UI preferences to API format
      const apiPreferences = careerService.convertToApiFormat(preferences, {
        jobRoles,
        experienceLevels,
        languages,
        interviewGoals
      });
      
      console.log('ResumeCareerSetupPage: Saving career preferences...');
      // Save preferences to API
      const result = await careerService.createPreferences(apiPreferences);
      
      if (!result) {
        throw new Error('Failed to save preferences. Empty response from server.');
      }
      
      console.log('ResumeCareerSetupPage: Career preferences saved successfully:', result._id);
      
      // Show success message and navigate to dashboard
      showToast('Preferences saved successfully!', 'success');
      
      // Call onComplete if provided, otherwise navigate
      if (onComplete) {
        onComplete();
      } else {
        console.log('ResumeCareerSetupPage -> onComplete not provided.');
      }
    } catch (err: any) {
      console.error('Failed to save preferences:', err);
      const errorMessage = err?.message || 'Failed to save preferences. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    // Show confirmation modal instead of immediately skipping
    setShowSkipConfirmation(true);
  };
  
  // New function to handle actual skip after confirmation
  const handleConfirmSkip = () => {
    setShowSkipConfirmation(false);
    showToast('Setup skipped. You can complete it later.', 'info');
    
    // Call onComplete if provided, otherwise navigate
    if (onComplete) {
      onComplete();
    } else {
      console.log('ResumeCareerSetupPage -> onComplete not provided.');
    }
  };
  
  // Function to cancel skip
  const handleCancelSkip = () => {
    setShowSkipConfirmation(false);
  };

  const isFormValid = () => {
    return (
      preferences.jobRoles.length > 0 && 
      preferences.experienceLevel && 
      preferences.languages.length > 0 && 
      preferences.interviewGoals.length > 0
    );
  };

  const handleCreateResume = () => {
    setIsUploadModalOpen(true);
  };
  
  const handleManageResumes = () => {
    window.open('https://dashboard.jobprot.com/seeker/dashboard', '_blank');
  };

  const handleOpenResumePreview = (resume: ResumeProtocol) => {
    setSelectedResume(resume);
    setIsResumeModalOpen(true);
  };

  const handleUploadSuccess = () => {
    // Refresh resumes after successful upload
    handleRefreshResumes();
  };

  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  // Error message for configuration loading failure
  if (configError) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
        <div className="container mx-auto px-4 pt-24 mt-6 pb-12">
          <div className="max-w-6xl mx-auto">
            {isLoadingConfig ? (
              <div className="bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark">
                <LoadingIndicator 
                  size="medium" 
                  text="Refreshing configurations..." 
                  items={['Job Roles', 'Experience Levels', 'Languages', 'Interview Goals']}
                  padding="large"
                />
              </div>
            ) : (
              <div className="bg-error-light/10 dark:bg-error-dark/10 border border-error-light/30 dark:border-error-dark/30 text-error-light dark:text-error-dark p-6 rounded-lg">
                <h2 className="text-xl font-bold mb-3">Configuration Error</h2>
                <p className="mb-4 text-base text-text-secondary-light dark:text-text-secondary-dark">{configError}</p>
                <div className="flex space-x-4">
                  <Button
                    variant="primary"
                    onClick={() => window.location.reload()}
                  >
                    Reload Page
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <main className="container mx-auto px-4 pt-24 mt-6 pb-12">
        <div className="max-w-6xl mx-auto">
          <PageTransitionItem index={1}>
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
                Set Up Your Profile Preferences
              </h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark text-base mb-4">
                Let us know about your career interests and preferences so we can tailor your interview practice experience.
              </p>
            </div>
          </PageTransitionItem>
          

          <PageTransitionItem index={3}>
            <div className="w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
              <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                Career Preferences
              </h2>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
                Tell us about your career goals and preferences so we can tailor your mock interview experience.
              </p>

              {isLoadingConfig ? (
                <div className="bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark">
                  <LoadingIndicator 
                    size="medium" 
                    text="Loading configurations..." 
                    items={['Job Roles', 'Experience Levels', 'Languages', 'Interview Goals']}
                    padding="large"
                  />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Job Roles */}
                  <div>
                    <div className="flex items-center mb-2">
                      <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Enter Job Roles</h3>
                    </div>
                    <div className="mt-2">
                      <FreeTextChipField
                        value={preferences.jobRoles}
                        suggestions={jobRoles.length > 0 
                          ? jobRoles.map(role => role.title || role.name).filter(Boolean) as string[]
                          : []
                        }
                        onChange={handleJobRolesChange}
                        placeholder="Type job roles and press Enter or comma to add (e.g., Software Engineer, Product Manager)"
                        maxItems={5}
                        helperText="Select upto 5 job roles, You can also paste comma-separated roles."
                      />
                    </div>
                  </div>

                  {/* Experience Level */}
                  <div>
                    <div className="flex items-center mb-2">
                      <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Your Experience Level</h3>
                    </div>
                    <div className="mt-2 mb-4">
                      <select
                        value={preferences.experienceLevel}
                        onChange={handleExperienceLevelChange}
                        className="w-full flex flex-wrap font-medium p-3 bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark text-sm border border-border-light dark:border-border-dark rounded-lg focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all min-h-[56px] appearance-none"
                      >
                        <option value="" disabled>
                          {experienceLevels.length > 0 ? "Select experience level..." : "Loading experience levels..."}
                        </option>
                        {experienceLevels.length > 0 && experienceLevels.map((level) => (
                          <option key={level._id} value={level.name}>
                            {level.name} - {level.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      Select your experience level, this will help us tailor the interview questions to your experience level
                    </p>
                  </div>

                  {/* Languages */}
                  <div>
                    <div className="flex items-center mb-2">
                      <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Select Languages</h3>
                    </div>
                    <div className="mt-2">
                      <AutocompleteChipField
                        value={preferences.languages}
                        options={languages.length > 0 ? languages.map(lang => lang.name) : []}
                        onChange={handleLanguagesChange}
                        placeholder={languages.length > 0 ? "Add languages..." : "Loading languages..."}
                        maxItems={5}
                      />
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Select the languages you're comfortable with
                    </p>
                  </div>

                  {/* Interview Goals */}
                  <div>
                    <div className="flex items-center mb-2">
                      <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Your Interview Goals</h3>
                    </div>
                    <div className="mt-2">
                      <AutocompleteChipField
                        value={preferences.interviewGoals}
                        options={interviewGoals.length > 0 ? interviewGoals.map(goal => goal.name) : []}
                        onChange={handleInterviewGoalsChange}
                        placeholder={interviewGoals.length > 0 ? "Add interview goals..." : "Loading interview goals..."}
                        maxItems={3}
                      />
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Select up to 3 interview goals. We'll use these to setup your interview preparation accordingly.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </PageTransitionItem>

          <PageTransitionItem index={2}>
            <div className="w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
              <div className="flex justify-between items-start mb-4">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
                     <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">Your Resumes</h2>
                    {resumes.length === 0 && <p className="p-2 px-3 bg-amber-500/10 text-amber-500 text-xs rounded-full font-medium font-heading">Optional, but good to have</p>}
                </div>
                
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRefreshResumes}
                  leftIcon={<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />}
                  loading={loading}
                >
                   Refresh
                </Button>
              </div>

              {error && (
                <div className="bg-error-light/10 dark:bg-error-dark/10 border border-error-light/30 dark:border-error-dark/30 text-error-light dark:text-error-dark p-4 rounded-lg mb-4">
                  <p className="text-sm">{error}</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRefreshResumes}
                    className="mt-2"
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {loading ? (
                <div className="py-8 flex justify-center">
                  <LoadingIndicator 
                    size="medium" 
                    text="Loading resumes..." 
                    padding="medium"
                  />
                </div>
              ) : !error && resumes.length === 0 ? (
                <div className="py-8 flex flex-col items-center text-center">
                  {/* Hidden on mobile using responsive state */}
                  {!isMobileView && (
                    <div className="bg-surface-light dark:bg-surface-dark rounded-full p-4 mb-4">
                      <FileText size={32} className="text-text-secondary-light dark:text-text-secondary-dark" />
                    </div>
                  )}
                  <h3 className="text-lg font-heading font-semibold mb-2">You have no resumes</h3>
                  <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-md text-sm">
                    Resume will be used to personalize your preparation journey. Upload one or create one with JobProt.
                  </p>
                  <div className="flex flex-col md:flex-row w-full md:w-auto justify-center gap-3">
                    <Button
                      variant="primary"
                      onClick={handleCreateResume}
                      leftIcon={<Upload size={18} />}
                      className="w-full md:w-auto"
                    >
                      Upload Resume
                    </Button>
                  <Button
                    variant="secondary"
                    onClick={() => window.open('https://dashboard.jobprot.com/resume-upload?action=create', '_blank')}
                    rightIcon={<ExternalLink size={18} />}
                    className="w-full md:w-auto"
                  >
                    Create With JobProt
                  </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4 -mt-2">
                      The following resume will be used to personalize your mock interviews:
                    </p>
                    
                    {resumes.sort((a, b) => new Date(b.updatedAt || '').getTime() - new Date(a.updatedAt || '').getTime()).map(resume => (
                      <div 
                        key={resume._id}
                        className="mb-4 p-3 md:p-4 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300 cursor-pointer"
                        onClick={() => handleOpenResumePreview(resume)}
                      >
                        <div className="flex items-start">
                          {!isMobileView && (
                            <div className="mr-4 bg-primary-light/10 dark:bg-primary-dark/10 p-2.5 rounded-full">
                              <FileText size={22} className="text-primary-light dark:text-primary-dark" />
                            </div>
                          )}
                          
                          <div className="flex-1">
                            <h3 className="font-heading font-semibold text-base text-text-primary-light dark:text-text-primary-dark mb-1">
                              {resume.personalDetails.name.split(' ')[0]} - {resume._id?.slice(-12)}
                            </h3>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                            {(() => {
                              if (resume.workExperience.length > 0) {
                                // First check if there's a current job
                                const currentJob = resume.workExperience.find(job => job.isCurrentJob);
                                
                                if (currentJob && currentJob.jobTitle && currentJob.companyName) {
                                  // Show current job
                                  return `${currentJob.jobTitle} at ${currentJob.companyName}`;
                                } else if (resume.workExperience[0]?.jobTitle && resume.workExperience[0]?.companyName) {
                                  // Show first job experience if no current job
                                  return `Worked at ${resume.workExperience[0].companyName} as ${resume.workExperience[0].jobTitle}`;
                                }
                              }
                              return 'No work experience added';
                            })()}
                            </p>
                            {isMobileView && (
                              <div className="mt-2 text-text-secondary-light dark:text-text-secondary-dark text-xs">
                                <span>Updated: {formatDate(resume.updatedAt || '')}</span>
                              </div>
                            )}
                          </div>
                          {!isMobileView && (
                            <div className="flex items-center text-xs text-text-tertiary-light dark:text-text-tertiary-dark rounded-2xl bg-background-light/60 dark:bg-background-dark/20 px-4 py-2">
                              <span>Updated: {formatDate(resume.updatedAt || '')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-end">
                    <Button
                      variant="secondary"
                      onClick={handleManageResumes}
                      rightIcon={<ExternalLink size={16} />}
                      className="w-full md:w-auto"
                    >
                      Manage Resumes
                    </Button>
                  </div>
                </>
              )}
            </div>
          </PageTransitionItem>


          {/* Validation Error Message */}
          {showValidationError && (
            <div className="mb-4 text-center">
              <p className="text-red-500 font-medium animate-fade-in">
                Please select your Career Preferences to continue
              </p>
            </div>
          )}

          <PageTransitionItem index={4}>
            <div className="flex flex-col md:flex-row justify-center md:justify-end space-y-4 md:space-y-0 md:space-x-4 order-last md:order-first">
              {resumes.length === 0 && (
              <Button
                variant="outline"
                onClick={handleSkip}
                className="w-auto md:px-12"
              >
                Do It Later
              </Button>
              )}
              <Button
                variant="primary"
                onClick={handleButtonClick}
                loading={isSaving || isLoadingConfig}
                disabled={!isFormValid() || isSaving || isLoadingConfig}
                rightIcon={isSaving ? null : (isFormValid() ? <ArrowRight size={16} /> : <Save size={16} />)}
                className="w-auto md:px-12"
              >
                {isSaving ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </PageTransitionItem>
        </div>
      </main>

      {/* Resume Preview Modal */}
      {selectedResume && (
        <ResumePreviewModal
          isOpen={isResumeModalOpen}
          onClose={() => setIsResumeModalOpen(false)}
          resume={selectedResume}
        />
      )}
      
      {/* Resume Upload Modal */}
      <ResumeUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />
      
      {/* Skip Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSkipConfirmation}
        title="Skip Profile Setup?"
        message="Are you sure you want to continue without setting up your career preferences? This information helps us tailor your mock interviews to your specific needs."
        confirmText="Yes, Skip for Now"
        cancelText="Continue Setup"
        confirmVariant="secondary"
        onConfirm={handleConfirmSkip}
        onCancel={handleCancelSkip}
      />
    </div>
  );
}

// Wrap the component with ToastProvider for notifications
const ResumeCareerSetupPage: React.FC<ResumeCareerSetupPageProps> = (props) => {
  return (
    <ToastProvider>
      <ResumeCareerSetupContent {...props} />
    </ToastProvider>
  );
};

ResumeCareerSetupPage.displayName = 'ResumeCareerSetupPage';

// Export with page transition HOC
export default withPageTransition(ResumeCareerSetupPage); 